export interface SubCategory {
  name: string;
  slug: string;
  jurisdiction_relevance: 'critical' | 'moderate' | 'not-needed';
  status: 'launch' | 'coming-soon';
  children?: { slug: string; name: string; h1?: string; intro?: string; metaTitle?: string; metaDescription?: string }[];
}

export interface Profession {
  id: string;
  name: string;
  slug: string;
  description: string;
  sub_categories: SubCategory[];
}

export interface Tool {
  id: string;
  name: string;
  slug: string;
  profession_slug: string;
  website_url: string;
  description_short: string;
  description_long: string;
  key_features: string[];
  pricing: { plan: string; price: string }[];
  affiliate_link: string;
  affiliate_details: string;
  categories: string[];
  tags: string[];
  is_sponsored: boolean;
  meta_title: string;
  meta_description: string;
  logo_url?: string;
  jurisdiction?: string;
  view_count?: number;
  created_at?: string;
}

export const professions: Profession[] = [
  {
    id: 'legal',
    name: 'Legal',
    slug: 'legal',
    description: 'Specialized AI tools for legal research, contract drafting, eDiscovery, and practice management.',
    sub_categories: [
      { name: 'Legal Research', slug: 'legal-research-tools', jurisdiction_relevance: 'critical', status: 'launch' },
      { name: 'Contract Drafting', slug: 'contract-drafting-ai', jurisdiction_relevance: 'moderate', status: 'launch' },
      { name: 'Contract Lifecycle Management', slug: 'contract-lifecycle-management', jurisdiction_relevance: 'moderate', status: 'launch' },
      { name: 'eDiscovery & Litigation', slug: 'ediscovery-litigation-ai', jurisdiction_relevance: 'critical', status: 'launch' },
      { name: 'Practice Management', slug: 'legal-practice-management-ai', jurisdiction_relevance: 'moderate', status: 'launch' },
      { name: 'Paralegal Tools', slug: 'paralegal-ai-tools', jurisdiction_relevance: 'moderate', status: 'launch' },
      { name: 'Legal Education', slug: 'legal-education-tools', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'Data Privacy', slug: 'data-privacy', jurisdiction_relevance: 'critical', status: 'launch' },
      { name: 'Environmental Protection Law', slug: 'environmental-protection-law', jurisdiction_relevance: 'critical', status: 'launch' },
      { name: 'Intellectual Property Law', slug: 'intellectual-property-law', jurisdiction_relevance: 'critical', status: 'launch', children: [
        { slug: 'patent-ai-tools', name: 'Patent AI Tools', h1: 'Patent AI Tools', intro: 'AI tools for patent drafting and analysis.', metaTitle: 'Best Patent AI Tools 2026', metaDescription: 'Discover the best AI tools for patent drafting.' },
        { slug: 'copyright-ai-tools', name: 'Copyright AI Tools', h1: 'Copyright AI Tools', intro: 'AI tools for copyright protection.', metaTitle: 'Best Copyright AI Tools 2026', metaDescription: 'Discover the best AI tools for copyright.' },
        { slug: 'trademark-ai-tools', name: 'Trademark AI Tools', h1: 'Trademark AI Tools', intro: 'AI tools for trademark search.', metaTitle: 'Best Trademark AI Tools 2026', metaDescription: 'Discover the best AI tools for trademark.' }
      ] },
    ]
  },
  {
    id: 'real-estate',
    name: 'Real Estate',
    slug: 'real-estate',
    description: 'AI-powered solutions for Lead Generation, Property Valuation, Listing Marketing, and Virtual Staging.',
    sub_categories: [
      { name: 'Lead Generation', slug: 'lead-generation-ai', jurisdiction_relevance: 'moderate', status: 'launch' },
      { name: 'Property Valuation', slug: 'property-valuation-ai', jurisdiction_relevance: 'critical', status: 'launch' },
      { name: 'Listing & Marketing', slug: 'listing-marketing-ai', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'Virtual Staging', slug: 'virtual-staging-ai', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'CRM & Workflow', slug: 'real-estate-crm-ai', jurisdiction_relevance: 'critical', status: 'launch' },
    ]
  },
  {
    id: 'finance',
    name: 'Accounts & Finance',
    slug: 'finance',
    description: 'Transformative AI for Bookkeeping, Tax Filing, Audit Compliance, and Financial Reporting.',
    sub_categories: [
      { name: 'Bookkeeping AI', slug: 'bookkeeping-ai-tools', jurisdiction_relevance: 'moderate', status: 'launch' },
      { name: 'Tax Filing AI', slug: 'tax-filing-ai', jurisdiction_relevance: 'critical', status: 'launch' },
      { name: 'Audit & Compliance', slug: 'audit-compliance-ai', jurisdiction_relevance: 'critical', status: 'launch', children: [{ slug: 'privacy-finance', name: 'Privacy Finance', h1: 'Privacy Finance AI Tools', intro: 'AI tools for privacy finance.', metaTitle: 'Best Privacy Finance AI Tools 2026', metaDescription: 'Discover the best AI tools for financial privacy.' }] },
      { name: 'Month-End Close', slug: 'month-end-close-ai', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'Financial Reporting', slug: 'financial-reporting-ai', jurisdiction_relevance: 'not-needed', status: 'launch', children: [{ slug: 'carbon-esg-finance', name: 'Carbon & ESG Finance', h1: 'Carbon & ESG Finance AI Tools', intro: 'AI tools for ESG compliance.', metaTitle: 'Best Carbon & ESG Finance AI Tools 2026', metaDescription: 'Discover the best AI tools for ESG reporting.' }] },
    ]
  },
  {
    id: 'education',
    name: 'Education',
    slug: 'education',
    description: 'Curated AI tools for teachers, students, universities, and immersive e-learning platforms.',
    sub_categories: [
      { name: 'AI for Teachers', slug: 'ai-tools-for-teachers', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'AI for Students', slug: 'ai-tools-for-students', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'AI for Universities', slug: 'ai-tools-for-universities', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'AI Learning Platforms', slug: 'elearning-ai-platforms', jurisdiction_relevance: 'not-needed', status: 'launch' },
      { name: 'Legal Academic', slug: 'legal-education-platforms', jurisdiction_relevance: 'critical', status: 'launch' },
    ]
  },
];

export function getToolUrlPath(profession_slug: string, categories: string[], slug: string): string {
  const primaryCategoryLabel = categories.length > 0 ? categories[0] : null;
  const secondaryCategoryLabel = categories.length > 1 ? categories[1] : null;
  const profession = professions.find(p => p.slug === profession_slug);
  
  let primarySlug = 'uncategorized';
  let nestedSlug = null;
  
  if (primaryCategoryLabel && profession) {
    const matched = profession.sub_categories.find(
      s => s.slug === primaryCategoryLabel || s.name.toLowerCase() === primaryCategoryLabel.toLowerCase()
    );
    if (matched) {
      primarySlug = matched.slug;
      if (secondaryCategoryLabel && matched.children) {
        const childMatch = matched.children.find(c => c.name.toLowerCase() === secondaryCategoryLabel.toLowerCase());
        if (childMatch) nestedSlug = childMatch.slug;
      }
    }
  }
  
  return nestedSlug ? `${primarySlug}/${nestedSlug}/${slug}` : `${primarySlug}/${slug}`;
}

export function getToolCanonicalUrl(profession_slug: string, categories: string[], slug: string): string {
  return `/en/ai-for-${profession_slug}/${getToolUrlPath(profession_slug, categories, slug)}`;
}

export const tools: Tool[] = [];


export function getSubCategorySlug(profession_slug: string, primaryCategoryLabel: string): string | null {
  const profession = professions.find((p) => p.slug === profession_slug);
  if (!profession) return null;
  const matchedSub = profession.sub_categories.find(
    (s) => s.slug === primaryCategoryLabel || s.name.toLowerCase() === primaryCategoryLabel.toLowerCase()
  );
  return matchedSub ? matchedSub.slug : null;
}
